/* Homepage model rotation, video disclosure playback and cart-count reconciliation. */
(function (window, document) {
  'use strict';

  if (window.__mmHomeFeaturedModelRotation) return;
  window.__mmHomeFeaturedModelRotation = true;

  if (window.Shopify && window.Shopify.designMode) return;

  const STORAGE_KEY = 'mm-home-featured-model-v1';
  const HERO_IMAGE_SIZES = '(min-width: 1300px) 620px, (min-width: 768px) 48vw, 92vw';
  const HERO_WIDTHS = [360, 540, 720, 900, 1200];

  function safeProductURL(value) {
    try {
      const url = new URL(value, window.location.href);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
      return url;
    } catch (_) {
      return null;
    }
  }

  function candidateKey(url) {
    return url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/products\/)/i, '').replace(/\/$/, '');
  }

  function resizedImageURL(value, width) {
    try {
      const url = new URL(value, window.location.href);
      if (url.protocol !== 'https:') return value;
      const shopifyCdn = /(^|\.)cdn\.shopify\.com$/i.test(url.hostname);
      const storefrontCdn = url.origin === window.location.origin && /^\/cdn\/shop\/(?:files|products)\//i.test(url.pathname);
      if (!shopifyCdn && !storefrontCdn) return value;
      url.searchParams.set('width', String(width));
      return url.href;
    } catch (_) {
      return value;
    }
  }

  function readPrevious() {
    try { return window.localStorage.getItem(STORAGE_KEY) || ''; }
    catch (_) { return ''; }
  }

  function savePrevious(value) {
    try { window.localStorage.setItem(STORAGE_KEY, value); }
    catch (_) { /* Rotation still works for this page when storage is blocked. */ }
  }

  // Store image data only: eager detached image clones can fetch every candidate.
  function imageSnapshot(image) {
    if (!image) return null;
    const source = image.getAttribute('src') || image.currentSrc || image.src;
    if (!source) return null;
    return {
      source: source,
      alt: image.getAttribute('alt') || '',
      width: image.getAttribute('width'),
      height: image.getAttribute('height')
    };
  }

  // Only the chosen model gets a new eager, high-priority hero image.
  function createHeroImage(snapshot) {
    const image = document.createElement('img');
    image.alt = snapshot.alt;
    if (snapshot.width) image.setAttribute('width', snapshot.width);
    if (snapshot.height) image.setAttribute('height', snapshot.height);
    image.loading = 'eager';
    image.decoding = 'async';
    image.setAttribute('fetchpriority', 'high');
    image.sizes = HERO_IMAGE_SIZES;
    image.srcset = HERO_WIDTHS.map(function (width) {
      return resizedImageURL(snapshot.source, width) + ' ' + width + 'w';
    }).join(', ');
    image.src = resizedImageURL(snapshot.source, 1200);
    return image;
  }

  function collectCandidate(link, image, title, price) {
    const url = safeProductURL(link && link.href);
    const cleanTitle = title && title.textContent.trim();
    const cleanPrice = price && price.textContent.trim();
    const cleanImage = imageSnapshot(image);
    if (!url || !cleanTitle || !cleanImage) return null;
    return {
      key: candidateKey(url),
      url: url.pathname + url.search,
      title: cleanTitle,
      price: cleanPrice || '',
      image: cleanImage
    };
  }

  function initializeCartCount(home) {
    if (home.dataset.mmCartCountReady === 'true' || typeof window.fetch !== 'function') return;
    const counts = document.querySelectorAll('.r32-header [data-r32-cart-count]');
    const cartLink = counts.length && counts[0].closest('a[href]');
    if (!cartLink) return;

    // The rendered cart link supplies the current storefront locale.
    let cartURL;
    try {
      cartURL = new URL(cartLink.href, window.location.href);
      if (cartURL.origin !== window.location.origin || !/\/cart\/?$/.test(cartURL.pathname)) return;
      cartURL.pathname = cartURL.pathname.replace(/\/$/, '') + '.js';
      cartURL.search = '';
      cartURL.hash = '';
    } catch (_) { return; }

    home.dataset.mmCartCountReady = 'true';
    let pending = false;
    let countRevision = 0;

    function applyCount(cart) {
      if (!cart || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return false;
      counts.forEach(function (count) { count.textContent = String(cart.item_count); });
      return true;
    }

    function refreshCount() {
      if (pending || document.visibilityState === 'hidden') return;
      pending = true;
      const requestedRevision = countRevision;
      const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      const timeout = controller ? window.setTimeout(function () { controller.abort(); }, 8000) : null;
      const options = { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } };
      if (controller) options.signal = controller.signal;
      window.fetch(cartURL.href, options)
        .then(function (response) {
          if (!response.ok) throw new Error('Cart count unavailable');
          return response.json();
        })
        .then(function (cart) { if (requestedRevision === countRevision) applyCount(cart); })
        .catch(function () { /* Keep the rendered count when the cart request is unavailable. */ })
        .finally(function () {
          if (timeout !== null) window.clearTimeout(timeout);
          pending = false;
        });
    }

    refreshCount();
    window.addEventListener('pageshow', function (event) { if (event.persisted) refreshCount(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshCount();
    });
    document.addEventListener('cart:updated', function (event) {
      if (applyCount(event.detail && event.detail.cart)) countRevision += 1;
      else refreshCount();
    });
  }

  function initialize() {
    const home = document.querySelector('[data-mm-homefront]');
    if (!home) return;
    initializeCartCount(home);

    // Closing the shop-video disclosure must also stop its hidden playback.
    home.querySelectorAll('.mm-fp-video').forEach(function (panel) {
      if (panel.dataset.mmVideoReady === 'true') return;
      panel.dataset.mmVideoReady = 'true';
      panel.addEventListener('toggle', function () {
        if (!panel.open) {
          panel.querySelectorAll('video').forEach(function (video) { video.pause(); });
        }
      });
    });

    if (home.dataset.mmFeaturedRotationReady === 'true') return;

    const hero = home.querySelector('.mm-fp-hero__media');
    const heroImage = hero && hero.querySelector(':scope > a img');
    const heroTitle = hero && hero.querySelector('.mm-fp-hero__caption strong');
    const heroPrice = hero && hero.querySelector('.mm-fp-hero__price');
    const heroPrimaryLink = hero && hero.querySelector(':scope > a[href]');
    if (!hero || !heroImage || !heroTitle || !heroPrimaryLink) return;

    const candidates = [];
    const seen = new Set();

    function add(candidate) {
      if (!candidate || seen.has(candidate.key)) return;
      seen.add(candidate.key);
      candidates.push(candidate);
    }

    add(collectCandidate(heroPrimaryLink, heroImage, heroTitle, heroPrice));

    home.querySelectorAll('.mm-fp-grid--products .mm-fp-product').forEach(function (card) {
      add(collectCandidate(
        card.querySelector('.mm-fp-product__media[href]'),
        card.querySelector('.mm-fp-product__media img'),
        card.querySelector('.mm-fp-product__title a'),
        card.querySelector('.mm-fp-product__price')
      ));
    });

    if (candidates.length < 2) return;
    home.dataset.mmFeaturedRotationReady = 'true';

    function apply(candidate) {
      const currentImage = hero.querySelector(':scope > a img');
      const title = hero.querySelector('.mm-fp-hero__caption strong');
      const price = hero.querySelector('.mm-fp-hero__price');
      const links = hero.querySelectorAll(':scope > a[href], .mm-fp-hero__caption > a[href]');
      if (!currentImage || !title || !links.length) return;

      links.forEach(function (link) { link.href = candidate.url; });
      title.textContent = candidate.title;
      if (price) {
        price.textContent = candidate.price;
        price.hidden = !candidate.price;
      }

      const arrow = hero.querySelector('.mm-fp-hero__caption > a[href]');
      if (arrow) {
        const prefix = /^es(?:-|$)/i.test(document.documentElement.lang || '') ? 'Ver ' : 'View ';
        arrow.setAttribute('aria-label', prefix + candidate.title);
      }

      currentImage.replaceWith(createHeroImage(candidate.image));
      hero.dataset.mmFeaturedModel = candidate.key;
      savePrevious(candidate.key);
    }

    function rotate() {
      const currentLink = hero.querySelector(':scope > a[href]');
      const currentURL = safeProductURL(currentLink && currentLink.href);
      const currentKey = currentURL ? candidateKey(currentURL) : '';
      const previousKey = readPrevious() || currentKey;
      let index = candidates.findIndex(function (candidate) { return candidate.key === previousKey; });
      if (index < 0) index = candidates.findIndex(function (candidate) { return candidate.key === currentKey; });
      apply(candidates[(index + 1 + candidates.length) % candidates.length]);
    }

    rotate();
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) rotate();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})(window, document);

